import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  FollowUpTodoResult,
  JobCreateRequest,
  JobCreationStatusResponse,
  JobCreationMessageStatus,
  JobStatusPayload,
} from '@techgitter/contracts';
import { createJob, getJobCreationStatus, JobCreationError } from '../services/jobCreationService';
import { onSignalREvent } from '../services/signalRService';

// Fallback polling constants (SignalR is the primary channel)
const FALLBACK_POLL_INTERVAL_MS = 1_500;
const FALLBACK_MAX_POLL_MS = 10_000;
const MAX_POLL_DURATION_MS = 180_000; // 3 minutes

export type SubmissionPhase = 'idle' | 'submitting' | 'accepted' | 'succeeded' | 'failed';

interface UseJobCreationResult {
  phase: SubmissionPhase;
  messageId: string | null;
  status: JobCreationStatusResponse | null;
  error: string | null;
  rsTicketId: string | null;
  rsTicketNumber: string | null;
  dvJobId: string | null;
  /** Per follow-up to-do in the request, whether it was created — reported
   *  in the 202. The ticket is accepted either way. */
  followUpResults: FollowUpTodoResult[];
  submit: (payload: JobCreateRequest) => Promise<void>;
  reset: () => void;
}

const TERMINAL_STATUSES: JobCreationMessageStatus[] = ['Succeeded', 'Failed', 'Dead Letter'];

export function useJobCreation(): UseJobCreationResult {
  const [phase, setPhase] = useState<SubmissionPhase>('idle');
  const [messageId, setMessageId] = useState<string | null>(null);
  const [status, setStatus] = useState<JobCreationStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [followUpResults, setFollowUpResults] = useState<FollowUpTodoResult[]>([]);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const pollStartRef = useRef(0);
  const resolvedRef = useRef(false);
  const msgIdRef = useRef<string | null>(null);
  const signalRUnsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      abortRef.current?.abort();
      signalRUnsubRef.current?.();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    signalRUnsubRef.current?.();
    signalRUnsubRef.current = null;
  }, []);

  const handleStatusUpdate = useCallback((statusResponse: JobCreationStatusResponse) => {
    if (resolvedRef.current) return;

    setStatus(statusResponse);

    if (TERMINAL_STATUSES.includes(statusResponse.status)) {
      resolvedRef.current = true;
      cleanup();

      if (statusResponse.status === 'Succeeded') {
        setPhase('succeeded');
      } else {
        setError(
          statusResponse.errors.length > 0
            ? statusResponse.errors.join('; ')
            : `Job creation ${statusResponse.status.toLowerCase()}`
        );
        setPhase('failed');
      }
    }
  }, [cleanup]);

  const startFallbackPolling = useCallback((msgId: string) => {
    const controller = new AbortController();
    abortRef.current = controller;
    pollStartRef.current = Date.now();

    const poll = async () => {
      if (resolvedRef.current) return;

      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        if (!resolvedRef.current) {
          resolvedRef.current = true;
          cleanup();
          setError('Ticket creation timed out. Check the Alerts page for status.');
          setPhase('failed');
        }
        return;
      }

      try {
        const statusResponse = await getJobCreationStatus(msgId, controller.signal);
        console.info('[useJobCreation] Fallback poll response: status=%s', statusResponse.status);
        handleStatusUpdate(statusResponse);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.warn('Fallback poll attempt failed:', err);
      }
    };

    const schedulePoll = (attempt: number) => {
      const delay = Math.min(
        FALLBACK_POLL_INTERVAL_MS * Math.pow(1.5, attempt),
        FALLBACK_MAX_POLL_MS
      );

      pollTimerRef.current = setTimeout(async () => {
        await poll();
        if (!resolvedRef.current) {
          schedulePoll(attempt + 1);
        }
      }, delay);
    };

    // Fire immediately — the POST already took seconds, orchestrator may be done
    poll().then(() => {
      if (!resolvedRef.current) {
        schedulePoll(0);
      }
    });
  }, [cleanup, handleStatusUpdate]);

  const startSignalRListener = useCallback(() => {
    console.info('[useJobCreation] SignalR listener registered (pre-submit)');
    const unsubscribe = onSignalREvent('jobs:status', (data: unknown) => {
      console.info('[useJobCreation] SignalR jobs:status event received:', data);
      const payload = data as JobStatusPayload | undefined;
      const currentMsgId = msgIdRef.current;
      if (!payload || !currentMsgId || payload.messageId !== currentMsgId) return;
      console.info('[useJobCreation] SignalR matched messageId, status=%s', payload.status);
      handleStatusUpdate(payload);
    });
    signalRUnsubRef.current = unsubscribe;
  }, [handleStatusUpdate]);

  const submit = useCallback(async (payload: JobCreateRequest) => {
    setPhase('submitting');
    setError(null);
    setStatus(null);
    setMessageId(null);
    setFollowUpResults([]);
    msgIdRef.current = null;
    resolvedRef.current = false;

    // Register SignalR listener BEFORE the POST so we don't miss early events
    startSignalRListener();

    try {
      const result = await createJob(payload);
      msgIdRef.current = result.messageId;
      setMessageId(result.messageId);
      setFollowUpResults(result.followUpTodos ?? []);
      setPhase('accepted');
      startFallbackPolling(result.messageId);
    } catch (err) {
      cleanup(); // Clean up SignalR listener on failure
      const errorMessage = err instanceof JobCreationError
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Failed to submit ticket';
      setError(errorMessage);
      setPhase('failed');
    }
  }, [startSignalRListener, startFallbackPolling, cleanup]);

  const reset = useCallback(() => {
    resolvedRef.current = true;
    cleanup();
    abortRef.current?.abort();
    setPhase('idle');
    setMessageId(null);
    setStatus(null);
    setError(null);
    setFollowUpResults([]);
  }, [cleanup]);

  return {
    phase,
    messageId,
    status,
    error,
    rsTicketId: status?.rsTicketId ?? null,
    rsTicketNumber: status?.rsTicketNumber ?? null,
    dvJobId: status?.dvJobId ?? null,
    followUpResults,
    submit,
    reset,
  };
}
